import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Brand, IServerApi, IVehicle, Vehicle} from '@service-and-repairs/awpintegrationlib';
import {
  VehicleSelectionOverlayComponent
} from '../../../components/vehicle/vehicle-selection-overlay/vehicle-selection-overlay.component';
import {AwpClientLibService} from '../../../services/awp-client-lib.service';
import {ServiceCase} from '../../service-case/models/service-case';
import {UserData} from '../../user/models/user-data';
import {UserService} from '../../user/services/user.service';
import {VehicleWarningLoader} from '../../vehicle-warning/services/vehicle-warning.loader';
import {VehicleService} from './vehicle.service';

@Injectable({
  providedIn: 'root'
})
export class VehicleLoader {
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly userService: UserService = inject(UserService);
  private readonly vehicleService: VehicleService = inject(VehicleService);
  private readonly vehicleWarningLoader: VehicleWarningLoader = inject(VehicleWarningLoader);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);

  vehicleSelectionOverlay: VehicleSelectionOverlayComponent;

  private readonly awpLib: IServerApi;

  constructor() {
    this.awpLib = this.awpClientLibService.serverApi;
  }

  static isValidVin7(vin: string): boolean {
    return vin?.length === 7;
  }

  static isValidVin17(vin: string): boolean {
    return vin?.length === 17;
  }

  static isValidTypeCode(code: string): boolean {
    return code?.length === 4;
  }

  async loadVehicleByVin(vin17: string, serviceCase: ServiceCase): Promise<void> {
    const user: UserData = this.userService.getCurrentUser();
    // called with VIN17, therefore only one vehicle can be returned
    const vehicles: IVehicle[] = await this.vehicleService.getVehicles(vin17, user);
    this.updateVehicleInServiceCase(serviceCase, vehicles[0]);
  }

  async loadVehicleInServiceCase(serviceCase: ServiceCase): Promise<boolean> {
    if (serviceCase.getVehicle() && !serviceCase.vehicleDataMissing()) {
      return this.updateVehicleInServiceCase(serviceCase, serviceCase.getVehicle());
    } else if (serviceCase.getVinLong() ?? serviceCase.getVinShort()) {
      return this.getVehicle(serviceCase);
    } else if (serviceCase.getTypeCode()) {
      return this.getVirtualVehicle(serviceCase);
    }
    return this.updateVehicleInServiceCase(serviceCase, null);
  }

  private updateVehicleInServiceCase(serviceCase: ServiceCase, vehicle: IVehicle): boolean {
    vehicle?.setLicensePlate(serviceCase.getLicensePlate());
    serviceCase.updateVehicle(vehicle);
    return this.vehicleWarningLoader.addVehicleWarnings(serviceCase);
  }

  private async selectVehicle(vehicles: IVehicle[]): Promise<IVehicle> {
    if (vehicles.length > 1) {
      try {
        return await this.vehicleSelectionOverlay.show(vehicles);
      } catch {
        this.awpLib.notifyWarning(this.translate.instant('vehicle.selectionAborted'));
        throw new Error();
      }
    }
    return vehicles[0];
  }

  private async getVehicle(serviceCase: ServiceCase): Promise<boolean> {
    const user: UserData = this.userService.getCurrentUser();
    const vin: string = serviceCase.getVinLong() ?? serviceCase.getVinShort();
    try {
      const vehicles: IVehicle[] = await this.vehicleService.getVehicles(vin, user);
      try {
        const vehicle: IVehicle = await this.selectVehicle(vehicles);
        return this.updateVehicleInServiceCase(serviceCase, vehicle);
      } catch {
        return this.updateVehicleInServiceCase(serviceCase, this.getUnidentifiedVehicle(serviceCase));
      }
    } catch {
      return this.handleFailedVehicleIdentification(serviceCase);
    }
  }

  private async getVirtualVehicle(serviceCase: ServiceCase): Promise<boolean> {
    const user: UserData = this.userService.getCurrentUser();
    try {
      const vehicle: IVehicle = await this.vehicleService.getVirtualVehicle(serviceCase.getTypeCode(), user);
      return this.updateVehicleInServiceCase(serviceCase, vehicle);
    } catch {
      return this.updateVehicleInServiceCase(serviceCase, this.getUnidentifiedVehicle(serviceCase));
    }
  }

  private getUnidentifiedVehicle(serviceCase: ServiceCase): IVehicle {
    return Vehicle.fromPlainObject({
      vin: serviceCase.getVinLong(),
      vin7: serviceCase.getVinShort(),
      typeCode: serviceCase.getTypeCode(),
      brand: this.toBrand(serviceCase.getBrand()),
      licensePlate: serviceCase.getLicensePlate(),
      modelDesignation: serviceCase.getModelDesignation(),
      developmentSeries: serviceCase.getDevelopmentCode(),
      imageUrlSmall: serviceCase.getSmallCosyImageUrl()
    });
  }

  private toBrand(brandValue: string | null): Brand {
    // The service case stores the brand value (e.g. 'BMW i'), not the enum key (e.g. 'BMW_i')
    return Object.values(Brand).find((brand: Brand): boolean => brand === brandValue) ?? Brand.UNKNOWN;
  }

  private handleFailedVehicleIdentification(serviceCase: ServiceCase): boolean {
    if (!serviceCase.getVinLong()) {
      // Vehicle identification failed and there is only a VIN7. Show warning as some AWP functionality
      // requires a VIN17.
      this.awpLib.notifyWarning(this.translate.instant('case.notificationVehicleLoadError'));
    }
    return this.updateVehicleInServiceCase(serviceCase, this.getUnidentifiedVehicle(serviceCase));
  }
}
